import { useEffect, useMemo, useState } from "react";
import { Command } from "cmdk";
import { ChevronRight, FilePlus2, FileText, Folder, History, LayoutTemplate, SquareCheckBig, TerminalSquare } from "lucide-react";
import type { WorkspaceItem } from "@hydra-notes/shared";
import { useAllSpaceItems } from "@/data/store";
import { useSharedFolders, sharedFoldersApi } from "@/data/sharedFolders";
import { searchService, type SearchHit } from "@/data/search/client";
import { useRecents } from "@/store/recents";
import { usePalette } from "@/store/palette";
import { openItem, createNote } from "@/commands/nav";
import { createFromTemplate, listTemplates } from "@/commands/templates";
import { allCommands, displayKeys, effectiveKeys, type AppCommand } from "@/commands/registry";
import { Kbd } from "@/ui/Kbd";
import s from "./Palette.module.css";

/**
 * One dialog, two modes (the Trilium pattern): note quick-switcher by
 * default; typing ">" (or Mod+Shift+P) switches to commands. Microbehaviors
 * copied from Obsidian: empty query lists recents, Enter opens, Shift+Enter
 * creates a note with the exact query as title, Mod+Enter opens in new tab.
 */
export function Palette() {
  const { mode, close, initialQuery } = usePalette();
  const open = mode !== "closed";
  const [value, setValue] = useState("");

  useEffect(() => {
    if (open && initialQuery) setValue(initialQuery);
  }, [open, initialQuery]);
  const spaces = useAllSpaceItems();
  const folders = useSharedFolders((st) => st.folders);
  const items = useMemo(() => spaces.flatMap((sp) => sp.items), [spaces]);
  // itemId -> shared folder title (result badges)
  const sharedBadge = useMemo(() => {
    const map = new Map<string, string>();
    for (const sp of spaces) {
      if (sp.kind !== "shared") continue;
      const title = folders.find((f) => f.id === sp.spaceId)?.title ?? "Shared";
      for (const i of sp.items) map.set(i.id, title);
    }
    return map;
  }, [spaces, folders]);
  const recents = useRecents((r) => r.ids);
  const [contentHits, setContentHits] = useState<SearchHit[]>([]);
  const [remoteHitIds, setRemoteHitIds] = useState<string[]>([]);

  const isCommandMode = mode === "commands" || (mode !== "template" && value.startsWith(">"));
  const isTemplateMode = mode === "template";
  const query = (isCommandMode ? value.replace(/^>\s*/, "") : value).trim();
  const tagQuery = !isCommandMode && !isTemplateMode && query.startsWith("#") ? query.slice(1).toLowerCase() : null;

  useEffect(() => {
    if (!open) {
      setValue("");
      setContentHits([]);
    }
  }, [open]);

  // content search (debounced) alongside instant title matching
  const [tagHitIds, setTagHitIds] = useState<string[]>([]);
  useEffect(() => {
    if (isCommandMode || !query) {
      setContentHits([]);
      setTagHitIds([]);
      return;
    }
    const t = setTimeout(() => {
      if (tagQuery !== null) {
        void searchService.notesWithTag(tagQuery).then(setTagHitIds);
      } else {
        void searchService.search(query, 20).then(setContentHits);
        // full text over shared folders lives server-side (they are
        // online-only, nothing of theirs is in the local index)
        void sharedFoldersApi
          .search(query)
          .then((r) => setRemoteHitIds(r.hits.map((h) => h.noteId)))
          .catch(() => setRemoteHitIds([]));
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, isCommandMode, tagQuery]);

  // Cards have their own search/open surface and must never fall through to
  // the ordinary note tab renderer.
  const live = useMemo(
    () => items.filter((i) => i.type !== "card" && i.type !== "cardboard" && !i.trashedAt && !i.archivedAt),
    [items],
  );

  const noteResults = useMemo(() => {
    if (isCommandMode || isTemplateMode) return { primary: [] as WorkspaceItem[], content: [] as WorkspaceItem[] };
    if (tagQuery !== null) {
      const tagged = new Set(tagHitIds);
      return {
        primary: live
          .filter((i) => i.tags.some((t) => t.toLowerCase().includes(tagQuery)) || tagged.has(i.id))
          .sort((a, b) => b.updatedAt - a.updatedAt)
          .slice(0, 20),
        content: [],
      };
    }
    if (!query) {
      const byId = new Map(live.map((i) => [i.id, i]));
      return {
        primary: recents.map((id) => byId.get(id)).filter((x): x is WorkspaceItem => !!x).slice(0, 12),
        content: [],
      };
    }
    const q = query.toLowerCase();
    const scoreText = (raw: string): number => {
      const t = (raw || "untitled").toLowerCase();
      if (t === q) return 100;
      if (t.startsWith(q)) return 80;
      if (t.includes(q)) return 60;
      if (isSubsequence(q, t)) return 30;
      return -1;
    };
    const scored = live
      .map((i) => {
        let score = Math.max(scoreText(i.title), ...(i.aliases ?? []).map(scoreText));
        if (score >= 0 && i.pinned) score += 15;
        return { i, score };
      })
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score || b.i.updatedAt - a.i.updatedAt)
      .slice(0, 12)
      .map((x) => x.i);
    const titleIds = new Set(scored.map((i) => i.id));
    const byId = new Map(live.map((i) => [i.id, i]));
    const content = [...contentHits.map((h) => h.id), ...remoteHitIds]
      .map((id) => byId.get(id))
      .filter((x): x is WorkspaceItem => !!x && !titleIds.has(x.id))
      .filter((x, idx, arr) => arr.indexOf(x) === idx)
      .slice(0, 8);
    return { primary: scored, content };
  }, [isCommandMode, isTemplateMode, tagQuery, query, live, recents, contentHits, remoteHitIds, tagHitIds]);

  const templateResults = useMemo(() => {
    if (!isTemplateMode) return [];
    const all = listTemplates();
    if (!query) return all;
    const q = query.toLowerCase();
    return all.filter((t) => t.title.toLowerCase().includes(q));
  }, [isTemplateMode, query]);

  const commandResults = useMemo(() => {
    if (!isCommandMode) return [];
    const cmds = allCommands().filter((c) => !c.hidden);
    if (!query) return cmds;
    const q = query.toLowerCase();
    return cmds.filter((c) => c.title.toLowerCase().includes(q) || isSubsequence(q, c.title.toLowerCase()));
  }, [isCommandMode, query]);

  const pathOf = (item: WorkspaceItem): string => {
    const parts: string[] = [];
    let cur = item.parentId;
    let hops = 0;
    while (cur && hops++ < 20) {
      const p = live.find((i) => i.id === cur);
      if (!p) break;
      parts.unshift(p.title || "Untitled");
      cur = p.parentId;
    }
    // shared items lead with their folder's name so results are unambiguous
    const badge = sharedBadge.get(item.id);
    if (badge) parts.unshift(badge);
    return parts.join(" / ");
  };

  const openNote = (id: string, newTab: boolean) => {
    openItem(id, { newTab });
    close();
  };

  if (!open) return null;

  return (
    <>
      <div className={s.overlay} onMouseDown={close} />
      <Command label="Quick switcher" shouldFilter={false} className={s.dialog}>
        <Command.Input
          autoFocus
          className={s.input}
          value={value}
          onValueChange={setValue}
          placeholder={
            isTemplateMode
              ? "Pick a template…"
              : isCommandMode
                ? "Run a command…"
                : "Find or create a note…  (> commands, # tags)"
          }
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              close();
            }
            if (!isCommandMode && e.key === "Enter" && e.shiftKey && query) {
              e.preventDefault();
              createNote(null, query);
              close();
            }
          }}
        />
        <Command.List className={s.list}>
          {isTemplateMode ? (
            <>
              {templateResults.length === 0 ? (
                <div className={s.empty}>
                  No templates. Create notes inside a root folder named “Templates”.
                </div>
              ) : null}
              {templateResults.map((t) => (
                <Command.Item
                  key={t.id}
                  value={t.id}
                  className={s.item}
                  onSelect={() => {
                    void createFromTemplate(t.id);
                    close();
                  }}
                >
                  <LayoutTemplate size={14} />
                  <span className={s.itemLabel}>{t.title || "Untitled"}</span>
                </Command.Item>
              ))}
            </>
          ) : isCommandMode ? (
            <>
              {commandResults.length === 0 ? <div className={s.empty}>No matching commands</div> : null}
              {commandResults.map((cmd) => (
                <CommandRow key={cmd.id} cmd={cmd} onRun={() => close()} />
              ))}
            </>
          ) : (
            <>
              {!query && noteResults.primary.length > 0 ? (
                <div className={s.groupHeading}>Recent</div>
              ) : null}
              {noteResults.primary.map((item) => (
                <Command.Item
                  key={item.id}
                  value={item.id}
                  className={s.item}
                  data-selected={undefined}
                  onSelect={() => openNote(item.id, false)}
                >
                  {item.type === "folder" ? (
                    <Folder size={14} />
                  ) : item.type === "tasklist" ? (
                    <SquareCheckBig size={14} />
                  ) : !query ? (
                    <History size={14} />
                  ) : (
                    <FileText size={14} />
                  )}
                  <span className={s.itemLabel}>{item.title || "Untitled"}</span>
                  <span className={s.itemPath}>{pathOf(item)}</span>
                </Command.Item>
              ))}
              {noteResults.content.length > 0 ? <div className={s.groupHeading}>Content matches</div> : null}
              {noteResults.content.map((item) => (
                <Command.Item
                  key={item.id}
                  value={item.id}
                  className={s.item}
                  onSelect={() => openNote(item.id, false)}
                >
                  <FileText size={14} />
                  <span className={s.itemLabel}>{item.title || "Untitled"}</span>
                  <span className={s.itemPath}>{pathOf(item)}</span>
                </Command.Item>
              ))}
              {query ? (
                <Command.Item value="__create__" className={s.item} onSelect={() => { createNote(null, query); close(); }}>
                  <FilePlus2 size={14} />
                  <span className={s.itemLabel}>Create “{query}”</span>
                  <span className={s.itemHint}>
                    <Kbd combo="Shift+Enter" />
                  </span>
                </Command.Item>
              ) : null}
              {!query && noteResults.primary.length === 0 ? (
                <div className={s.empty}>Nothing recent. Type to search or create.</div>
              ) : null}
            </>
          )}
        </Command.List>
        <div className={s.footer}>
          <span className={s.footerHint}>
            <Kbd combo="Enter" /> open
          </span>
          {!isCommandMode ? (
            <>
              <span className={s.footerHint}>
                <Kbd combo="Shift+Enter" /> create
              </span>
              <span className={s.footerHint}>
                <ChevronRight size={11} /> commands
              </span>
            </>
          ) : (
            <span className={s.footerHint}>
              <TerminalSquare size={11} /> command mode
            </span>
          )}
        </div>
      </Command>
    </>
  );
}

function CommandRow({ cmd, onRun }: { cmd: AppCommand; onRun: () => void }) {
  const keys = effectiveKeys(cmd);
  return (
    <Command.Item
      value={cmd.id}
      className={s.item}
      onSelect={() => {
        cmd.run();
        onRun();
      }}
    >
      <TerminalSquare size={14} />
      <span className={s.itemLabel}>{cmd.title}</span>
      {keys ? (
        <span className={s.itemHint}>
          <Kbd combo={displayKeys(keys)} />
        </span>
      ) : null}
    </Command.Item>
  );
}

function isSubsequence(needle: string, haystack: string): boolean {
  let i = 0;
  for (const ch of haystack) {
    if (ch === needle[i]) i++;
    if (i === needle.length) return true;
  }
  return needle.length === 0;
}
